import { useForm as useAntForm } from "antd/es/form/Form";
import { Authenticated, useTranslate } from "@refinedev/core";
import { Flex, Modal, Typography } from "antd";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { PageLayout } from "components/PageLayout";
import DeleteAccount from "./components/DeleteAccount";
import ChangePassword from "./components/ChangePassword";
import { CreatorDnaCard } from "./components/CreatorDnaCard";
import { ProfileDetailsCard } from "./components/ProfileDetailsCard";
import { ProfileSectionCard } from "./components/ProfileSectionCard";
import { ProfileSideNav, ProfileNavItem } from "./components/ProfileSideNav";
import useDeleteAccount from "./hooks/useDeleteAccount";
import { canChangePassword, canUseMfa } from "./helpers";
import { useCreatorProfile } from "pages/creator-profile/hooks/useCreatorProfile";
import { useSession } from "hooks/useSession";
import { authService } from "services/authService/";
import { Button } from "@libertify/ui";
import { TwoFactorAuthSection } from "pages/settings/security";

export const UserProfilePage = () => {
  const t = useTranslate();
  const authProvider = authService.getProvider();
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const { handleDeleteAccount, isLoading } = useDeleteAccount();
  const [form] = useAntForm();
  const [deleteAccountButtonDisabled, setDeleteAccountButtonDisabled] =
    useState(true);
  useEffect(() => {
    form.resetFields();
  }, [deleteAccountModalOpen, form]);

  // SSO accounts render neither the 2FA nor the password section, so their
  // anchors are dropped as well instead of pointing at nothing.
  const showTwoFactorAuth = canUseMfa(authProvider);
  const showChangePassword = canChangePassword(authProvider);

  // The DNA only exists on teacher profiles, and only once the user has been
  // through the onboarding questionnaire. While that's still resolving the
  // section is kept so the page doesn't jump, then dropped if there's nothing.
  const { user } = useSession();
  const {
    profile,
    isLoading: isLoadingProfile,
    isError: isProfileError,
  } = useCreatorProfile({ userId: user?.id });
  const teacherProfile = profile?.type === "teacher" ? profile : undefined;
  const showCreatorDna =
    !isProfileError && (isLoadingProfile || !!teacherProfile);

  const navItems: ProfileNavItem[] = [
    { id: "profile-details", label: t("pages.user.edit.nav.profileDetails") },
    ...(showCreatorDna
      ? [{ id: "creator-dna", label: t("pages.user.edit.nav.creatorDna") }]
      : []),
    ...(showTwoFactorAuth
      ? [
          {
            id: "two-factor-auth",
            label: t("pages.user.edit.nav.twoFactorAuth"),
          },
        ]
      : []),
    ...(showChangePassword
      ? [
          {
            id: "change-password",
            label: t("pages.user.edit.nav.changePassword"),
          },
        ]
      : []),
  ];

  return (
    <Authenticated key="user_edit">
      <PageLayout title={t("pages.user.edit.profile")}>
        <Flex gap={40} align="flex-start">
          <StyledSideNav>
            <ProfileSideNav items={navItems} />
          </StyledSideNav>

          <StyledSections vertical gap={24}>
            <ProfileDetailsCard id="profile-details" />

            {showCreatorDna ? (
              <CreatorDnaCard id="creator-dna" profile={teacherProfile} />
            ) : null}

            {showTwoFactorAuth ? (
              <ProfileSectionCard
                id="two-factor-auth"
                title={t("pages.user.edit.twoFactorAuth")}
                subtitle={t("pages.user.edit.twoFactorAuthSubtitle")}
              >
                <TwoFactorAuthSection />
              </ProfileSectionCard>
            ) : null}

            {showChangePassword ? (
              <ProfileSectionCard
                id="change-password"
                title={t("pages.user.changePassword.title")}
              >
                <ChangePassword />
              </ProfileSectionCard>
            ) : null}

            <ProfileSectionCard
              tone="danger"
              title={t("pages.user.edit.deleteAccount")}
              subtitle={t("pages.user.edit.deleteAccountWarning")}
              action={
                <Button
                  variant="danger"
                  onClick={() => setDeleteAccountModalOpen(true)}
                >
                  {t("pages.user.edit.deleteAccount")}
                </Button>
              }
            />
          </StyledSections>
        </Flex>
      </PageLayout>

      <Modal
        okButtonProps={{
          danger: true,
          disabled: deleteAccountButtonDisabled,
        }}
        confirmLoading={isLoading}
        okText={t("pages.user.edit.deleteAccount")}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onOk={async () => {
          try {
            await handleDeleteAccount();
            setDeleteAccountModalOpen(false);
          } catch (_error) {
            // Error notification is handled by Refine's auth system
            // Modal stays open so user can try again
            console.error("Error deleting account", _error);
          }
        }}
        open={deleteAccountModalOpen}
        onCancel={() => setDeleteAccountModalOpen(false)}
        title={t("pages.user.edit.deleteAccount")}
      >
        {/* until the backend supports the use case */}
        {authProvider === "scalekit" ? (
          <Typography.Text>{t("src.App.contactSupport")}</Typography.Text>
        ) : (
          <DeleteAccount
            form={form}
            onChange={(isValid) => {
              setDeleteAccountButtonDisabled(!isValid);
            }}
          />
        )}
      </Modal>
    </Authenticated>
  );
};

// ─── Styled components ───

const StyledSideNav = styled.div`
  flex: 0 0 200px;

  @media (width <= 768px) {
    display: none;
  }
`;

const StyledSections = styled(Flex)`
  flex: 1;
  min-width: 0;
`;
